import { List, Map } from "immutable";
import { createSelector } from "reselect";
import { sortCompare } from "~/utils/utils.jsx";
import { getAllProjectTemplateElements } from "./selector.projectTemplateElements";

const getAllProjectTemplateTaskDependencies = (state) => state.project_template_task_dependencies;

export const getAllParentTemplateTaskDependencies = (state) => getAllProjectTemplateTaskDependencies(state).parents;

export const getAllChildTemplateTaskDependencies = (state) => getAllProjectTemplateTaskDependencies(state).children;

const defaultGroupedSuggestions = Map();
const getAllTaskDependenciesGroupedSuggestions = (state) =>
  getAllProjectTemplateTaskDependencies(state).groupedSuggestions || defaultGroupedSuggestions;

const defaultParents = List();
export const makeGetAllParentsByProjectTemplateTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllParentTemplateTaskDependencies(state).get(`${id}`, defaultParents),
    (state) => getAllProjectTemplateElements(state),
    (parentsIds, elements) => {
      return parentsIds
        .map((id) => elements[id])
        .filter(Boolean)
        .sort((a, b) => {
          if (!a.start_on && b.start_on) return 1;
          if (a.start_on && !b.start_on) return -1;

          const startCompare = sortCompare(a.start_on, b.start_on);
          if (startCompare !== 0) return startCompare;
          return sortCompare(a.name.toLowerCase(), b.name.toLowerCase());
        })
        .map((el) => el.id)
        .toArray();
    }
  );
const defaultChildren = List();

export const makeGetAllChildrenByProjectTemplateTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllChildTemplateTaskDependencies(state).get(`${id}`, defaultChildren),
    (state) => getAllProjectTemplateElements(state),
    (childrenIds, elements) => {
      return childrenIds
        .map((id) => elements[id])
        .filter(Boolean)
        .sort((a, b) => {
          if (!a.start_on && b.start_on) return 1;
          if (a.start_on && !b.start_on) return -1;

          const startCompare = sortCompare(a.start_on, b.start_on);
          if (startCompare !== 0) return startCompare;
          return sortCompare(a.name.toLowerCase(), b.name.toLowerCase());
        })
        .map((el) => el.id)
        .toArray();
    }
  );

export const makeGetTemplateTaskHasDependencies = () =>
  createSelector(
    makeGetAllParentsByProjectTemplateTaskIdSelector(),
    makeGetAllChildrenByProjectTemplateTaskIdSelector(),
    (parents, children) => parents.length + children.length > 0
  );

export const makeGetIsProjectTemplateTaskParent = () =>
  createSelector(
    makeGetAllParentsByProjectTemplateTaskIdSelector(),
    (state, id, dependencyId) => dependencyId,
    (parents, dependencyId) => parents.indexOf(dependencyId) > -1
  );

export const makeGetSortedProjectTemplateTaskIdsSelector = () =>
  createSelector(
    (state, ids) => ids.map((id) => getAllProjectTemplateElements(state).get(`${id}`)),
    (tasks) => {
      tasks = tasks.filter((t) => t && t.id);
      return tasks
        .filter((task) => task)
        .sort((a, b) => sortCompare(a.position, b.position))
        .sort((a, b) => {
          const result = sortCompare(a.start_on, b.start_on);

          if (result !== 0) {
            if (!a.start_on) return 1;
            if (!b.start_on) return -1;
          }

          return result;
        })
        .map((task) => task.id);
    }
  );

export const makeGetAllGroupedSuggestionsByTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllTaskDependenciesGroupedSuggestions(state).get(`${id}`),
    (state) => state.project_template_elements,
    (groupedSuggestions, templateElements) => {
      const tasks = Object.values(templateElements).filter((element) => element.class === "ProjectTemplateTask");
      const taskLists = Object.values(templateElements).filter(
        (element) => element.class === "ProjectTemplateTaskList"
      );
      const unsorted =
        groupedSuggestions && groupedSuggestions.size > 0
          ? groupedSuggestions?.reduce((acc, value, key) => {
              const taskList = taskLists.find((tl) => tl["id"] === +key);
              if (taskList?.id) {
                const unsortedTasks = value.reduce((tasksAcc, value1) => {
                  const task = tasks.length ? tasks.find((t) => t.id === +value1) : {};
                  if (task?.id) {
                    return [
                      ...tasksAcc,
                      {
                        id: task?.id,
                        name: task?.name,
                        position: task?.position,
                      },
                    ];
                  }
                  return tasksAcc;
                }, []);
                const sortedTasks = unsortedTasks
                  ?.sort((a, b) => sortCompare(a.position, b.position))
                  .map((a) => {
                    return { id: a.id, name: a.name };
                  });
                return [
                  ...acc,
                  {
                    id: taskList?.id,
                    name: taskList?.name,
                    position: taskList?.position,
                    options: sortedTasks,
                  },
                ];
              }
              return acc;
            }, [])
          : [];

      return unsorted
        ? unsorted
            .sort((a, b) => sortCompare(a.position, b.position))
            .map((a) => {
              return { id: a.id, name: a.name, options: a.options };
            })
        : [];
    }
  );
